import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import useModal from 'hooks/useModal';
import { CompanyData, CompanyModel, CompanyType } from 'models/CompanyModel';
import { UserData } from 'models/UserModel';
import CompanyDialog from 'modules/companies/dialogs/CompanyDialog';
import InviteAdminDialog from 'modules/users/dialogs/InviteAdminDialog';
import VesselSelectDialog from 'modules/companies/dialogs/VesselSelectDialog';
import auth from 'recoil/atoms/auth';
import companyList from 'recoil/atoms/companyList';
import Api from 'utils/api';
import { replaceItemAtIndex } from 'utils/common';


const useCompanyModals = (type: CompanyType) => {
    const {t} = useTranslation();
    const {showModal, hideModal} = useModal();
    const setAuth = useSetRecoilState(auth);
    const setCompanies = useSetRecoilState(companyList(type));

    const createCompany = (company: CompanyData, admin: UserData) =>
        Api.company.create(type, company, admin)
            .then(res => setCompanies(old => [res.data.data, ...old]))

    const updateMyCompany = (company: CompanyModel) =>
        Api.auth.updateCompany(company).then(() => setAuth(old => ({...old, company})));

    const updateCompany = (company: CompanyModel) =>
        Api.company.update(company)
            .then(res => {
                setCompanies(old => replaceItemAtIndex(old, old.findIndex(i => i.id === company.id), res.data.data));
            });

    const openAddCompanyModal = () => showModal(CompanyDialog,
        {
            action: t('add'),
            company: {
                country: 'JP',
                utcOffset: 540,
                type,
            } as CompanyModel,
        },
        {
            title: t('create_company', {type: t(type)}),
        })
        .then(company => showModal(InviteAdminDialog,
            {},
            {
                title: t('invite_first_user'),
                maxWidth: 'xs',
            })
            .then(admin => createCompany(company, admin))
        )
        .then(() => hideModal(-2));

    const openEditCompanyModal = (company: CompanyModel, my?: boolean) => showModal(CompanyDialog,
        {
            action: t('save'),
            company
        },
        {
            title: t('edit_title', {value: t('company')})
        })
        .then(company => {
            const act = my ? updateMyCompany : updateCompany;
            return act(company)
        })
        .then(hideModal);

    const openVesselSelectModal = (company: CompanyModel) => showModal(VesselSelectDialog,
        {saved: company.vessels?.map(vessel => vessel.id) || []},
        {
            title: '',
            maxWidth: 'xs',
        })
        .then(vessels => Api.company.setVessels(company.id, vessels))
        .then(res => {
            setCompanies(old => replaceItemAtIndex(old, old.findIndex(i => i.id === company.id), res.data));
        })
        .then(hideModal);

    return {
        openAddCompanyModal,
        openEditCompanyModal,
        openVesselSelectModal,
    };
};

export default useCompanyModals;
